import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ApproversInfo } from "@/shared/components/ApproversInfo";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { useMySetupApprovals } from "../hooks/useSetupApprovals";
import type { SetupApprovalRequest } from "../services/setupApprovalApi";
import { ResubmitDialog } from "../components/setupApprovals/ResubmitDialog";

const formatDate = (value?: string) => {
  if (!value) return "\u2014";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatusBadge = ({ status }: { status: string }) => {
  switch (status) {
    case "Pending":
      return (
        <Badge variant="outline" className="text-amber-600 border-amber-300">
          Pending
        </Badge>
      );
    case "Approved":
      return (
        <Badge variant="outline" className="text-green-600 border-green-300">
          Approved
        </Badge>
      );
    case "Rejected":
      return (
        <Badge variant="destructive">
          Rejected
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const TypeBadge = ({ type }: { type: string }) => {
  const colours: Record<string, string> = {
    Pricing: "bg-blue-100 text-blue-800",
    CropType: "bg-green-100 text-green-800",
    CropVariety: "bg-purple-100 text-purple-800",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        colours[type] ?? "bg-gray-100 text-gray-800"
      }`}
    >
      {type === "CropType" ? "Crop Type" : type === "CropVariety" ? "Crop Variety" : type}
    </span>
  );
};

const formatKey = (k: string) => k.replace(/([A-Z])/g, " $1").trim();

const formatValue = (v: unknown): string => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
};

const ObjectFields = ({ obj }: { obj: Record<string, unknown> }) => (
  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
    {Object.entries(obj)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted-foreground capitalize">{formatKey(k)}</dt>
          <dd className="font-medium whitespace-pre-wrap">{formatValue(v)}</dd>
        </div>
      ))}
  </dl>
);

const JsonPreview = ({ json }: { json: string }) => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return <p className="text-xs text-muted-foreground">{json}</p>;
  }

  if (Array.isArray(parsed)) {
    return (
      <div className="space-y-3">
        {parsed.map((item, i) => (
          <div key={i} className="rounded-md border border-muted p-3">
            <p className="text-xs font-semibold text-muted-foreground mb-2">Entry {i + 1}</p>
            {typeof item === "object" && item !== null ? (
              <ObjectFields obj={item as Record<string, unknown>} />
            ) : (
              <p className="text-xs">{String(item)}</p>
            )}
          </div>
        ))}
      </div>
    );
  }

  if (typeof parsed === "object" && parsed !== null) {
    return <ObjectFields obj={parsed as Record<string, unknown>} />;
  }

  return <p className="text-xs text-muted-foreground">{String(parsed)}</p>;
};

export const MySubmissionsPage = () => {
  const { data: submissions, isLoading, error } = useMySetupApprovals();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [resubmitting, setResubmitting] = useState<SetupApprovalRequest | null>(null);

  if (isLoading) return <LoadingAnimation fullScreen />;

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load your submissions</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">My Submissions</h1>
        <p className="text-muted-foreground mt-1">
          Track your setup change requests and resubmit rejected ones
        </p>
        <div className="mt-2">
          <ApproversInfo permission="crop_types.approve" label="Who can approve" />
        </div>
      </div>

      {(!submissions || submissions.length === 0) ? (
        <div className="rounded-lg border bg-muted/30 py-16 text-center">
          <p className="text-muted-foreground">No submissions yet</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Submitted</TableHead>
                <TableHead>Reviewed</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {submissions.map((req) => (
                <>
                  <TableRow
                    key={req.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => setExpanded(expanded === req.id ? null : req.id)}
                  >
                    <TableCell>
                      <TypeBadge type={req.approvalTypeName} />
                    </TableCell>
                    <TableCell className="font-medium">{req.description}</TableCell>
                    <TableCell>
                      <StatusBadge status={req.statusName} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(req.requestedAt)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {req.reviewedByUserName
                        ? `${req.reviewedByUserName} — ${formatDate(req.reviewedAt)}`
                        : "\u2014"}
                    </TableCell>
                    <TableCell className="text-right">
                      {req.statusName === "Rejected" && (
                        <Button
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setResubmitting(req);
                          }}
                        >
                          Amend & Resubmit
                        </Button>
                      )}
                      {req.statusName === "Pending" && (
                        <span className="text-xs text-muted-foreground">Awaiting Review</span>
                      )}
                    </TableCell>
                  </TableRow>

                  {expanded === req.id && (
                    <TableRow key={`${req.id}-detail`}>
                      <TableCell colSpan={6} className="bg-muted/20 px-6 py-4">
                        {req.statusName === "Rejected" && req.rejectionReason && (
                          <div className="mb-3 rounded-md bg-red-50 border border-red-200 p-3">
                            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">
                              Rejection Reason
                            </p>
                            <p className="text-sm text-red-600">{req.rejectionReason}</p>
                          </div>
                        )}
                        {req.statusName === "Approved" && req.reviewNotes && (
                          <div className="mb-3 rounded-md bg-green-50 border border-green-200 p-3">
                            <p className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-1">
                              Review Notes
                            </p>
                            <p className="text-sm text-green-600">{req.reviewNotes}</p>
                          </div>
                        )}
                        <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
                          Proposed {req.existingEntityId ? "Update" : "New Record"}
                        </p>
                        <JsonPreview json={req.entityJson} />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {resubmitting && (
        <ResubmitDialog
          request={resubmitting}
          open={!!resubmitting}
          onOpenChange={(v) => !v && setResubmitting(null)}
        />
      )}
    </div>
  );
};
